import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import RoomTutorial from './room/tutorial/RoomTutorial';
import * as PIXI from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import {
  isWallMountedItem,
} from '../domain/tileMath';
import type { RenderFurniture } from '../domain/types';
import { useUserData } from '../hooks/useUserData';
import useInterfaceStore from '../stores/useInterfaceStore';
import useFurniturePreviewStore from '../stores/useFurniturePreviewStore';
import { useRoomHistory } from './room/state/useRoomHistory';
import { useRoomQueries, useRoomDerivedFurniture } from './room/useRoomData';
import { makeGreenOutlineGraphic } from './room/render/overlays';
import { BASE_RADIAL_MENU_OPTIONS,PREVIEW_BUY_MENU_OPTION, PREVIEW_BUY_ALL_MENU_OPTION, PREVIEW_CANCEL_MENU_OPTION, PREVIEW_END_TESTDRIVE_OPTION, PREVIEW_MIRROR_MENU_OPTION, ADMIN_SET_FOOTPRINT_OPTION, ADMIN_SET_SURFACE_OPTION, WALL_HEIGHT_UP_OPTION, WALL_HEIGHT_DOWN_OPTION } from './room/interaction/radialMenu';
import { useRadialMenu } from './room/interaction/useRadialMenu';
import { useRoomActions } from './room/useRoomActions';
import { useFurnitureInteraction } from './room/interaction/useFurnitureInteraction';
import { useFurnitureRender } from './room/useFurnitureRender';
import { useRoomScene, type RoomPixiRoots } from './room/useRoomScene';
import { createRoomSceneState } from './room/state/roomSceneState';
import { useRoomTextureSwap } from './room/useRoomTextureSwap';
import { useAlphaMaskCache } from './room/state/useAlphaMaskCache';
import { useFurnitureDiagnostics } from './room/useFurnitureDiagnostics';
import { useRoomDecoLongPress } from './room/interaction/useRoomDecoLongPress';
import { useRoomOverlays } from './room/useRoomOverlays';
import { installFootprintTool } from './room/dev/footprintTool';
import { useRoomCharacter } from './room/useRoomCharacter';
import { useCobwebSync } from './room/useCobwebSync';
import { useViewportDragSelect } from './room/interaction/useViewportDragSelect';
import { useTelegramBackButton } from '../hooks/useTelegramBackButton';
import { isPracticeRoom } from '../domain/interiorTutorial';
import { askSkipPractice } from './room/tutorial/practiceRoom';
import { useOnboardingState } from '../hooks/useOnboardingState';
import { mapSceneSingleton } from './map/mapScene';
import RoomTopLeftButtons from './room/components/RoomTopLeftButtons';
import UndoRedoControls from './room/components/UndoRedoControls';
import GridModeToggle from './room/components/GridModeToggle';
import GodModeToggle from './room/components/GodModeToggle';
import BuildModeToggle from './room/components/BuildModeToggle';
import AdminTilePanel from './room/components/AdminTilePanel';
import CityHallFurnitureInfoModal from './room/components/CityHallFurnitureInfoModal';
import ShopInventoryButtons from './room/components/ShopInventoryButtons';
import RoomPanel from './room/components/RoomPanel';
import { rememberLastHouse } from './map/mapPreload';
import PreviewPurchaseModal from './room/components/PreviewPurchaseModal';
import RadialMenuOverlay from './room/components/RadialMenuOverlay';
import RoomOnboardingModal from './room/components/RoomOnboardingModal';
import { flushLayout } from './room/state/layoutSync';
import { focusCameraOnItem } from './room/render/focusCamera';
import {
  FLOOR_HEIGHT,
  TILE_WIDTH,
  TILE_HEIGHT,
} from '../domain/grid';

// WALL_ANCHOR_Y moved to ./room/useRoomScene.ts (sole consumer: scene-init effect).

// DRAG_THRESHOLD moved to ./room/useFurnitureInteraction.ts
// (sole consumer). LONG_PRESS_* stay here — also used by the cobweb long-press below.
const LONG_PRESS_THRESHOLD_MS = 420;
const LONG_PRESS_MOVE_THRESHOLD = 10;
// ADMIN_DOUBLE_CLICK_THRESHOLD_MS / ADMIN_DOUBLE_CLICK_DISTANCE_THRESHOLD moved to
// ./room/useViewportDragSelect.ts (sole consumer: viewport drag-select effect).

// Grid geometry (FLOOR_*, TILE_*, MAP_WIDTH_TILES, BASE_MAP_*, GRID_SUBDIVISION)
// lives in ../domain/grid.ts — single source of truth.
// Pure tile/footprint math (estimateFootprintTilesFromSpriteWidth, estimateRugFootprintTiles,
// mirrorFpOffset, isRugCategory, isWallMountedItem, estimateWindowFootprintTiles, getTileDims,
// getWindowWallSide) lives in ../domain/tileMath.ts.

// SURFACE_ZINDEX_BOOST + CONFLICT_FLOOR_ZINDEX moved to ./room/useFurnitureInteraction.ts
// (sole consumer: the drag/drop interaction cluster).
// SURFACE_PLACEMENT_DELTA relocated to ../domain/depthMath.ts (sole consumer: recomputeAllZIndexes)

// SurfaceTileEntry, SurfaceTileMap, checkSurfaceOverlap, isFullyOnSurface live in ../domain/depthMath.ts.

// Windows are positioned higher on the wall (not on the floor).
// This offset lifts them vertically in world coordinates.
// Imported from windowSnap.ts for consistency
// WINDOW_VERTICAL_OFFSET lives in ../domain/windowSnap.

// Room world size for viewport (enough to contain the room with padding)
const ROOM_WORLD_SIZE = 1600;

// RenderFurniture lives in ../domain/types.ts.

// AlphaMask type + applyAlphaMaskToSprite extracted to ./room/render/alphaMask.ts

// DepthComponents lives in ../domain/depthMath.ts.

// RADIAL_OPTION_MIN_WIDTH / RADIAL_OPTION_HEIGHT moved to ./room/components/RadialMenuOverlay.tsx (sole consumer).
// STAR_ICON / SHELL_ICON moved to ./room/components/PreviewPurchaseModal.tsx (sole consumer).
// DEBUG_HIDE_FURNITURE_SPRITES moved to ./room/useFurnitureRender.ts (sole consumer).

// Розміри меблів = розмір зображення / ROOM_ART_SCALE (room/render/textures.ts),
// обчислюються при завантаженні текстури

// Room scene state (reuses the map singleton app)
let roomViewport: Viewport | null = null;
let roomContainer: PIXI.Container | null = null;
let rugContainer: PIXI.Container | null = null; // Rugs render below all other furniture
let interiorContainer: PIXI.Container | null = null;
let cobwebContainer: PIXI.Container | null = null; // Cobweb sprites (above furniture, below admin overlays)
// Tracks live PIXI sprites per cobweb ID so the effect can add/remove incrementally.
// posKey encodes the cobweb's current position — if it changes (e.g. furniture data
// loaded after first render), the sprite is destroyed and recreated at the new position.
let cobwebSpriteMap = new Map<number, { outline: PIXI.Container; graphic: PIXI.Container; posKey: string }>();

// Live get/set accessor over the six scene-state `let` bindings above. Passed to
// useRoomScene (the extracted scene-init effect) so its reads AND writes hit these
// exact module bindings — the extracted effect hooks (useRoomOverlays, useCobwebSync,
// useViewportDragSelect) read/write them through this same accessor.
const roomPixiRoots: RoomPixiRoots = {
  get roomViewport() { return roomViewport; },
  set roomViewport(v) { roomViewport = v; },
  get roomContainer() { return roomContainer; },
  set roomContainer(v) { roomContainer = v; },
  get rugContainer() { return rugContainer; },
  set rugContainer(v) { rugContainer = v; },
  get interiorContainer() { return interiorContainer; },
  set interiorContainer(v) { interiorContainer = v; },
  get cobwebContainer() { return cobwebContainer; },
  set cobwebContainer(v) { cobwebContainer = v; },
  get cobwebSpriteMap() { return cobwebSpriteMap; },
  set cobwebSpriteMap(v) { cobwebSpriteMap = v; },
};

// Stable ref shell around roomPixiRoots so hooks can write via .current
// (react-hooks/immutability allows *Ref.current mutations; bare object params do not).
const roomPixiRootsRef: { current: RoomPixiRoots } = { current: roomPixiRoots };
const getTutorialRoomContainer = () => roomPixiRoots.roomContainer;
const getTutorialViewport = () => roomPixiRoots.roomViewport;

// Build a spider-web PIXI.Graphics for the given radius, color and alpha.
// Defined at module level so it can be shared by the incremental cobweb effect without
// being recreated on every render.
// makeCobwebGraphic extracted to ./room/render/cobweb.ts

interface RoomCanvasProps {
  mapObjectId: number;
}

// Interior undo/redo snapshot helpers (toSnapshot, snapshotSignature) extracted to ./room/history.ts

const RoomCanvas: React.FC<RoomCanvasProps> = ({ mapObjectId }) => {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const { data: userData } = useUserData();
  const { hasOnboardingCompleted, data: onboardingQueryData } = useOnboardingState();
  const { currentRoomOwnerId, setMapView, justPlacedFurnitureCatalogId, setJustPlacedFurnitureCatalogId } = useInterfaceStore();
  const {
    previewMapObjectId,
    previewItems,
  } = useFurniturePreviewStore();
  // Cobweb sweep sound cluster (useSound + fade helper + freshness refs) moved to
  // ./room/useCobwebSync.ts together with the cobweb sync effect (sole consumer).

  const [isSceneReady, setIsSceneReady] = useState(false);
  const [selectedFurniture, setSelectedFurniture] = useState<RenderFurniture | null>(null);
  const [cityHallFurnitureInfo, setCityHallFurnitureInfo] = useState<RenderFurniture | null>(null);
  const [showGridMode, setShowGridMode] = useState(false);
  const buildMode = useInterfaceStore((s) => s.roomBuildMode);
  const roomPanel = useInterfaceStore((s) => s.roomPanel);
  const setBuildMode = useInterfaceStore((s) => s.setRoomBuildMode);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  // 'furniture' | 'cobweb' — discriminates what the shared radial menu is currently for
  // Admin tile selection state
  type AdminTileMode = 'footprint' | 'surface' | null;
  const [adminTileMode, setAdminTileMode] = useState<AdminTileMode>(null);
  const [adminTileCatalogId, setAdminTileCatalogId] = useState<number | null>(null);
  // Selected tiles as Set of "dx,dy" strings relative to furniture center tile
  const [adminSelectedTiles, setAdminSelectedTiles] = useState<Set<string>>(new Set());
  const [adminTileSaving, setAdminTileSaving] = useState(false);

  // The interior tutorial's practice room (browser-only): the player owns it, no admin tools.
  const practice = isPracticeRoom(mapObjectId);
  // Preloaded on the next launch (mapPreload.ts).
  useEffect(() => {
    if (!practice) rememberLastHouse(mapObjectId);
  }, [mapObjectId, practice]);
  const isOwner = practice || (userData && currentRoomOwnerId !== null &&
    String(userData.user_id) === String(currentRoomOwnerId));
  const isAdmin = !practice && (userData?.is_admin ?? false);
  const [isRoomGodMode, setIsRoomGodMode] = useState(false);
  const effectiveIsOwner = isOwner || (isAdmin && isRoomGodMode);

  // Telegram back button handler
  const handleBack = () => {
    if (practice) askSkipPractice();
    else setMapView();
  };

  useTelegramBackButton(true, handleBack);

  // Show the furniture guide for owners only while they are still onboarding.
  // Gate on onboarding_completed (the single source of truth) — NOT the
  // deprecated map_onboarding_month step flag, which can be unset even for
  // fully-onboarded users, making the guide re-appear forever on every room
  // (re-)entry (e.g. after returning from the furniture shop).
  // (c) isSceneReady is PIXI scene readiness — open guide only after external render is ready.
  // hasOnboardingCompleted is a fresh closure each render; gate via onboarding query data
  // identity so we re-run when server onboarding state arrives without thrashing every render.
  useEffect(() => {
    if (isOwner && !practice && isSceneReady && !hasOnboardingCompleted()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- wait for PIXI scene ready
      setShowOnboardingModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasOnboardingCompleted is unstable; onboardingQueryData is the data identity
  }, [isOwner, isSceneReady, onboardingQueryData]);

  // Room data fetches (house furniture / catalog / map objects / cobwebs) + the
  // isCityHall memo — extracted verbatim to ./room/useRoomData.ts (useRoomQueries).
  const {
    houseFurniture,
    houseFurnitureLoaded,
    houseFurnitureFetching,
    furnitureCatalog,
    mapObjects,
    cobwebsData,
    isCityHall,
  } = useRoomQueries({ mapObjectId });

  const canManageFurniture = !!(effectiveIsOwner || (isAdmin && isCityHall));

  // Room PIXI scene mutable state (was ~40 useRefs).
  // Single object grouped by meaning. createRoomSceneState() runs once as the
  // useRef initial value — same lifecycle as the previous individual useRefs.
  // captureSuspended lives on sceneRef.current.drag (needed by useRoomHistory below).
  const sceneRef = useRef(createRoomSceneState());

  // Layout edits are local-first (room/state/layoutSync.ts); leaving the room sends
  // whatever is still queued right away instead of waiting for the quiet period.
  useEffect(() => () => { void flushLayout(mapObjectId); }, [mapObjectId]);

  // Undo/redo history (session-scoped, in-memory).
  // drag.captureSuspended suspends auto-capture for multi-write drag gestures.
  const { canUndo, canRedo, handleUndo, handleRedo } = useRoomHistory({
    mapObjectId, houseFurniture, houseFurnitureLoaded, houseFurnitureFetching,
    canManageFurniture, sceneRef, removeGreenOutline,
  });

  // Derived furniture (catalog enrichment, cobweb layout, preview + placed lists)
  // extracted verbatim to ./room/useRoomData.ts (useRoomDerivedFurniture).
  const { cobwebs, previewRenderableFurniture, placedFurniture } = useRoomDerivedFurniture({
    mapObjectId,
    houseFurniture,
    furnitureCatalog,
    mapObjects,
    cobwebsData,
    previewMapObjectId,
    previewItems,
  });

  // Initial wall/floor catalog-ids effect + texture swap effect moved to
  // ./room/useRoomTextureSwap.ts — the hook is called below, after the refs it
  // needs are declared, preserving the original effect registration order.

  // Detect newly added test-drive (preview) items and queue them for green-outline highlight.
  // Must be declared BEFORE the markers useEffect so it runs first in effect order.
  useEffect(() => {
    const currentIds = new Set(previewItems.map(p => p.instanceId));
    for (const id of currentIds) {
      if (!sceneRef.current.highlight.prevPreviewCatalogIds.has(id)) {
        // Find the catalogId for the newly added instance to highlight
        const item = previewItems.find(p => p.instanceId === id);
        if (item) sceneRef.current.highlight.pendingCatalogId = item.catalogId;
        break;
      }
    }
    sceneRef.current.highlight.prevPreviewCatalogIds = currentIds;
  }, [previewItems]);

  // Transfer justPlacedFurnitureCatalogId from store → ref so the render loop highlights it
  useEffect(() => {
    if (justPlacedFurnitureCatalogId !== null) {
      sceneRef.current.highlight.pendingCatalogId = justPlacedFurnitureCatalogId;
      setJustPlacedFurnitureCatalogId(null);
    }
  }, [justPlacedFurnitureCatalogId, setJustPlacedFurnitureCatalogId]);

  const getRadialMenuOptions = useCallback((furniture: RenderFurniture | null) => {
    // Practice room: the tutorial teaches Flip; storing or selling practice items makes no sense.
    if (practice) return BASE_RADIAL_MENU_OPTIONS.filter(o => o.key === 'mirror');
    if (furniture?.is_preview) {
      const cat = furniture?.furniture_catalog?.category;
      // Room decorations (walls/floors): Buy + End Test Drive only
      if (cat === 'walls' || cat === 'floors') {
        const opts = [PREVIEW_BUY_MENU_OPTION, PREVIEW_END_TESTDRIVE_OPTION];
        if (previewItems.length > 1) opts.push(PREVIEW_BUY_ALL_MENU_OPTION);
        return opts;
      }
      // For preview items: Buy, Cancel, Mirror (skip mirror for wall-mounted)
      const isWallMounted = isWallMountedItem(furniture?.furniture_catalog);
      const previewBase = isWallMounted
        ? [PREVIEW_BUY_MENU_OPTION, PREVIEW_CANCEL_MENU_OPTION, WALL_HEIGHT_UP_OPTION, WALL_HEIGHT_DOWN_OPTION]
        : [PREVIEW_BUY_MENU_OPTION, PREVIEW_CANCEL_MENU_OPTION, PREVIEW_MIRROR_MENU_OPTION];
      if (previewItems.length > 1) previewBase.push(PREVIEW_BUY_ALL_MENU_OPTION);
      if (isAdmin) {
        const adminExtras = isWallMounted
          ? [ADMIN_SET_FOOTPRINT_OPTION, ADMIN_SET_SURFACE_OPTION]
          : [ADMIN_SET_FOOTPRINT_OPTION, ADMIN_SET_SURFACE_OPTION];
        return [...previewBase, ...adminExtras];
      }
      return previewBase;
    }
    const isWallMounted = isWallMountedItem(furniture?.furniture_catalog);
    const baseOptions = isWallMounted
      ? [...BASE_RADIAL_MENU_OPTIONS.filter(o => o.key !== 'mirror'), WALL_HEIGHT_UP_OPTION, WALL_HEIGHT_DOWN_OPTION]
      : BASE_RADIAL_MENU_OPTIONS;
    if (isAdmin) {
      const adminExtras = isWallMounted
        ? [ADMIN_SET_FOOTPRINT_OPTION, ADMIN_SET_SURFACE_OPTION]
        : [ADMIN_SET_FOOTPRINT_OPTION, ADMIN_SET_SURFACE_OPTION];
      return [...baseOptions, ...adminExtras];
    }
    return baseOptions;
  }, [isAdmin, practice, previewItems.length]);

  // Individual scene refs folded into sceneRef.current (roomSceneState.ts).

  // Wall/floor texture swap effect moved to ./room/useRoomTextureSwap.ts (see hook call below).

  // ── Wall/floor texture swap + initial backdrop catalog ids ──
  // Extracted verbatim to ./room/useRoomTextureSwap.ts (final cleanup pass). Called
  // here — after the refs it receives are declared — so both effects still register
  // ahead of the scene-init effect (useRoomScene below), as before.
  useRoomTextureSwap({
    isSceneReady,
    isCityHall,
    placedFurniture,
    sceneRef,
  });

  const tileGridDebugEnabled = useMemo(() => {
    // Show grid overlay when grid mode is enabled
    return showGridMode;
  }, [showGridMode]);

  // Keep refs in sync with state to avoid closure issues
  useEffect(() => {
    sceneRef.current.flags.showGridMode = showGridMode;
  }, [showGridMode]);

  useEffect(() => {
    sceneRef.current.flags.buildMode = buildMode;
  }, [buildMode]);

  useEffect(() => {
    if (!buildMode && showGridMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- leaving build mode must hide the grid
      setShowGridMode(false);
    }
  }, [buildMode, showGridMode]);

  useEffect(() => {
    if (!canManageFurniture && buildMode) {
      sceneRef.current.flags.buildMode = false;
      // Permissions revoke build mode immediately (a store action, stable identity).
      setBuildMode(false);
    }
  }, [canManageFurniture, buildMode, setBuildMode]);

  useEffect(() => {
    sceneRef.current.admin.tileMode = adminTileMode;
  }, [adminTileMode]);

  useEffect(() => {
    sceneRef.current.admin.selectedTiles = adminSelectedTiles;
  }, [adminSelectedTiles]);

  // Alpha-mask decode cache (ensureAlphaMaskLoaded / peekAlphaMask) extracted
  // verbatim to ./room/useAlphaMaskCache.ts (final cleanup pass); the in-flight
  // dedupe map (alphaMaskLoadingRef) moved in with it (sole consumer).
  const { ensureAlphaMaskLoaded, peekAlphaMask } = useAlphaMaskCache({ sceneRef });

  // recomputeAllZIndexes lives in ../domain/depthMath.ts (surface-boost-aware two-pass).

  // Diagnostic console dump (logFurnitureDiagnostics) extracted verbatim to
  // ./room/useFurnitureDiagnostics.ts (final cleanup pass).
  const logFurnitureDiagnostics = useFurnitureDiagnostics({ sceneRef, TILE_HEIGHT });

  // Occupied-tiles overlay redraw moved to ./room/useRoomOverlays.ts (called below,
  // right after useRoomScene — see the ordering note at the call site).

  // Radial menu state machine extracted to ./room/useRadialMenu (decomposition step 04c).
  // `showGreenOutline` is a hoisted function declaration (defined later in the body),
  // so referencing it here at render time is safe. `roomViewport` is module-level
  // mutable state, passed as a getter to preserve live reads in frozen closures.
  const {
    showRadialMenu,
    menuPosition,
    highlightedRadialAction,
    setHighlightedRadialAction,
    detectRadialActionByScreenPoint,
    activateMarkerMenuVisualState,
    closeRadialMenu,
    openRadialMenuCore,
  } = useRadialMenu({
    setSelectedFurniture,
    showGreenOutline,
    getRoomViewport: () => roomViewport,
  });

  // Furniture variant — sets furniture-specific state, activates marker visual, then delegates to core.
  const openFurnitureRadialMenu = useCallback(
    (marker: PIXI.Container, furniture: RenderFurniture, pointerX: number, pointerY: number) => {
      const options = getRadialMenuOptions(furniture);
      setSelectedFurniture(furniture);

      activateMarkerMenuVisualState(marker);
      openRadialMenuCore(options, pointerX, pointerY);
    },
    [openRadialMenuCore, getRadialMenuOptions, activateMarkerMenuVisualState]
  );

  // Room-deco (walls/floors) variant — finds active preview, sets state, then delegates to core.
  const openRoomDecoRadialMenu = useCallback(
    (category: 'walls' | 'floors', pointerX: number, pointerY: number) => {
      const previewItem = previewRenderableFurniture.find(f => f.furniture_catalog?.category === category);
      if (!previewItem) return;
      setSelectedFurniture(previewItem);

      if (roomViewport) roomViewport.pause = false;
      openRadialMenuCore([PREVIEW_BUY_MENU_OPTION, PREVIEW_END_TESTDRIVE_OPTION], pointerX, pointerY);
      try { window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light'); } catch { /* optional */ }
    },
    [openRadialMenuCore, previewRenderableFurniture]
  );

  // ── Wall/floor (room-deco) long-press radial-menu cluster ──
  // radialMenuHandlersRef + attachSimpleLongPress + the wall/floor attach effect
  // extracted verbatim to ./room/useRoomDecoLongPress.ts (final cleanup pass). The
  // hook is called at the same point of the render, so the per-render handlers-ref
  // update and the effect registration order are unchanged. executeRadialAction is
  // wrapped in an arrow to avoid a temporal dead zone crash (it is declared later
  // in the component body by the useRoomActions() call).
  useRoomDecoLongPress({
    isSceneReady,
    scene: roomPixiRoots,
    previewRenderableFurniture,
    detectRadialActionByScreenPoint,
    executeRadialAction: (key, f) => executeRadialAction(key, f), // arrow wrapper avoids TDZ
    closeRadialMenu,
    setHighlightedRadialAction,
    openRoomDecoRadialMenu,
    sceneRef,
    LONG_PRESS_THRESHOLD_MS,
    LONG_PRESS_MOVE_THRESHOLD,
  });

  // (unmount cleanup for the radial-menu marker visual state now lives in useRadialMenu)

  // ── Scene-init effect (room PIXI scene setup/teardown) ──
  // Extracted verbatim to ./room/useRoomScene.ts (decomposition step: scene-init
  // effect). roomViewport / roomContainer / rugContainer / interiorContainer /
  // cobwebContainer / cobwebSpriteMap are module-level mutable scene state that
  // the effect both reads AND writes, so it is passed as `roomPixiRoots` — a
  // live get/set accessor object over those bindings (declared next to them
  // above); the other effects here keep reading the module `let`s directly.
  useRoomScene({
    mapObjectId,
    isCityHall,
    effectiveIsOwner,
    showGridMode,
    tileGridDebugEnabled,
    FLOOR_HEIGHT,
    ROOM_WORLD_SIZE,
    pixiRootsRef: roomPixiRootsRef,
    sceneRef,
    canvasContainerRef,
    setIsSceneReady,
  });

  // ── Grid/admin overlays, occupied-tiles redraw, viewport re-centering, admin drag pause ──
  // Extracted verbatim to ./room/useRoomOverlays.ts (final cleanup pass). Called right
  // after useRoomScene so the grid-overlay / admin-overlay / re-center effects keep
  // registering after the scene-init effect and before the cobweb sync below —
  // preserving the original registration order of every co-dependent effect pair.
  // (The occupied-tiles redraw and admin drag pause moved in with the cluster: their
  // co-dependent ref-sync effects above still register first, as before.)
  // DEV-ONLY: window.__fpTool for authoring furniture footprints (see the module).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    return installFootprintTool(sceneRef, () => roomViewport);
  }, []);

  useRoomOverlays({
    isSceneReady,
    mapObjectId,
    isCityHall,
    showGridMode,
    tileGridDebugEnabled,
    adminTileMode,
    adminSelectedTiles,
    FLOOR_HEIGHT,
    ROOM_WORLD_SIZE,
    scene: roomPixiRoots,
    sceneRef,
  });

  // ── Sims-style character: click-to-move, easystarjs paths, feet-anchor depth ──
  // Added after the overlays cluster so it registers behind every effect that
  // builds the scene it attaches to (the floor sprite and interiorContainer must
  // already exist). It only adds listeners; it never replaces the floor's own.
  // Character disabled on interior open (house + city hall) per 2026-08-29 request.
  useRoomCharacter({
    enabled: false,
    isSceneReady,
    mapObjectId,
    pixiRootsRef: roomPixiRootsRef,
    sceneRef,
  });

  // ── Cobweb sprite sync + sweep sound cluster ──
  // Extracted verbatim to ./room/useCobwebSync.ts (final cleanup pass). Module scene
  // state (roomContainer / cobwebContainer / cobwebSpriteMap) is read AND written
  // through the roomPixiRoots accessor; the singleton renderer is passed as a getter
  // so live reads are preserved at effect run time.
  useCobwebSync({
    mapObjectId,
    isSceneReady,
    showGridMode,
    cobwebs,
    pixiRootsRef: roomPixiRootsRef,
    getRenderer: () => mapSceneSingleton.app?.renderer,
    sceneRef,
  });

  // ── Furniture render effect (async chunked render pass) ──
  // Extracted verbatim to ./room/useFurnitureRender.ts (decomposition step:
  // furniture-render effect). interiorContainer/rugContainer are module-level
  // mutable scene state, passed as getters to preserve live reads at effect run
  // time. setupFurnitureInteraction is wrapped in an arrow to avoid a temporal
  // dead zone crash (it is declared below by the useFurnitureInteraction() call).
  useFurnitureRender({
    mapObjectId,
    placedFurniture,
    flags: {
      canManageFurniture,
      isCityHall,
      isSceneReady,
      effectiveIsOwner,
      tileGridDebugEnabled,
    },
    tileSize: {
      TILE_WIDTH,
      TILE_HEIGHT,
    },
    scene: {
      getInteriorContainer: () => interiorContainer,
      getRugContainer: () => rugContainer,
    },
    sceneRef,
    alphaMasks: {
      ensureAlphaMaskLoaded,
      peekAlphaMask,
    },
    callbacks: {
      setCityHallFurnitureInfo,
      setupFurnitureInteraction: (sprite, furniture, marker) => setupFurnitureInteraction(sprite, furniture, marker), // arrow wrapper avoids TDZ
      showGreenOutline,
      focusNewItem: (marker) => focusCameraOnItem(roomViewport, marker),
      logFurnitureDiagnostics,
    },
  });

  // Admin double-click handler for room coordinates (similar to MapCanvas)
  // ── Viewport drag-select / admin tile-editor pointer effect ──
  // Extracted verbatim to ./room/useViewportDragSelect.ts (decomposition step:
  // viewport drag-select effect). roomViewport / roomContainer are read via the
  // roomPixiRoots accessor; removeGreenOutline is hoisted (function declaration),
  // so passing it directly is safe (no TDZ arrow-wrapper needed).
  useViewportDragSelect({
    isSceneReady,
    isAdmin,
    pixiRootsRef: roomPixiRootsRef,
    sceneRef,
    removeGreenOutline,
    setAdminSelectedTiles,
  });

  // Admin-tile-editor viewport drag pause moved to ./room/useRoomOverlays.ts (called above).

  // Setup drag and tap for a furniture sprite
  // ── Furniture interaction (drag / drop / tap radial menu) ──
  // Extracted verbatim to ./room/useFurnitureInteraction.ts (decomposition step:
  // setupFurnitureInteraction). roomContainer/roomViewport are module-level mutable
  // scene state, passed as getters to preserve live reads inside event handlers.
  // executeRadialAction is wrapped in an arrow to avoid a temporal dead zone crash
  // (it is declared below by the useRoomActions() call).
  const { setupFurnitureInteraction } = useFurnitureInteraction({
    mapObjectId,
    access: {
      canManageFurniture,
      houseFurnitureLoaded,
      houseFurnitureFetching,
      tileGridDebugEnabled,
    },
    scene: {
      getRoomContainer: () => roomContainer,
      getRoomViewport: () => roomViewport,
    },
    sceneRef,
    radialMenu: {
      openFurnitureRadialMenu,
    },
    visualFeedback: {
      activateMarkerMenuVisualState,
      removeGreenOutline,
      showGreenOutline,
      logFurnitureDiagnostics,
    },
  });

  // Action cluster (mirror / unplace / radial dispatch / preview purchase) extracted
  // verbatim to ./room/useRoomActions.ts. The purchase-modal state
  // (previewPurchaseFurniture / InstanceId / Loading) and the TDZ-breaking
  // handlePreviewBuyAllRef moved into the hook together with the cluster.
  const {
    previewPurchaseFurniture,
    setPreviewPurchaseFurniture,
    setPreviewPurchaseInstanceId,
    previewPurchaseLoading,
    executeRadialAction,
    handlePreviewBuy,
  } = useRoomActions({
    mapObjectId,
    isAdmin,
    closeRadialMenu,
    sceneRef,
    setAdminTileCatalogId,
    setAdminTileMode,
    setAdminSelectedTiles,
    logFurnitureDiagnostics,
  });

  // Green outline helpers
  // function declaration (hoisted): referenced by the useRadialMenu() call above.
  function showGreenOutline(marker: PIXI.Container, strong = false) {
    removeGreenOutline();
    // makeGreenOutlineGraphic extracted to ./room/render/overlays.ts
    const gfx = makeGreenOutlineGraphic(marker.getLocalBounds(), strong);
    marker.addChild(gfx);
    sceneRef.current.highlight.greenOutline = gfx;
  }

  // function declaration (hoisted): referenced by the useRoomHistory() call above.
  function removeGreenOutline() {
    if (sceneRef.current.highlight.greenOutline) {
      sceneRef.current.highlight.greenOutline.destroy();
      sceneRef.current.highlight.greenOutline = null;
    }
    sceneRef.current.highlight.marker = null;
  };

  // The selection outline is an editor affordance: it must not linger once the
  // owner leaves build mode.
  useEffect(() => {
    if (!buildMode) removeGreenOutline();
  }, [buildMode]);

  return (
    <div
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        // iOS: no selection loupe / callout on a long press while arranging furniture.
        WebkitTouchCallout: 'none',
      }}
    >
      {/* Canvas container */}
      <div
        ref={canvasContainerRef}
        onContextMenu={(e) => e.preventDefault()}
        style={{ width: '100%', height: '100%' }}
      />

      {/* Back + Help/Guide buttons (top-left) */}
      <RoomTopLeftButtons onBack={handleBack} onHelp={() => setShowOnboardingModal(true)} />

      {/* Build-mode toggle (hammer + furniture icon). */}
      {canManageFurniture && (
        <BuildModeToggle buildMode={buildMode} onToggle={() => setBuildMode(!buildMode)} />
      )}

      {/* Undo / Redo controls — available while editing the interior.
          Sits on the right, stacked under the grid / god-mode toggles. */}
      {canManageFurniture && buildMode && (
        <UndoRedoControls
          isAdmin={isAdmin}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={handleUndo}
          onRedo={handleRedo}
        />
      )}

      {/* Grid Mode toggle button - shows/hides tile grid overlay */}
      {buildMode && (
        <GridModeToggle showGridMode={showGridMode} onToggle={() => setShowGridMode(!showGridMode)} />
      )}

      {/* God mode toggle - admin only, gives owner-level rights in this room */}
      {isAdmin && (
        <GodModeToggle isRoomGodMode={isRoomGodMode} onToggle={() => setIsRoomGodMode(!isRoomGodMode)} />
      )}

      {/* Admin tile selection mode panel */}
      {isAdmin && adminTileMode !== null && (
        <AdminTilePanel
          mapObjectId={mapObjectId}
          adminTileMode={adminTileMode}
          adminSelectedTiles={adminSelectedTiles}
          adminTileSaving={adminTileSaving}
          setAdminTileSaving={setAdminTileSaving}
          adminTileCatalogId={adminTileCatalogId}
          setAdminTileMode={setAdminTileMode}
          setAdminTileCatalogId={setAdminTileCatalogId}
          setAdminSelectedTiles={setAdminSelectedTiles}
          sceneRef={sceneRef}
          getRoomViewport={() => roomViewport}
        />
      )}

      {/* City Hall furniture info modal (single-click for visitors) */}
      {cityHallFurnitureInfo && cityHallFurnitureInfo.furniture_catalog && (
        <CityHallFurnitureInfoModal
          furniture={cityHallFurnitureInfo}
          onClose={() => setCityHallFurnitureInfo(null)}
        />
      )}

      {/* Furniture Shop and Inventory buttons (owner or admin in city hall) — shown only while build mode is on */}
      {canManageFurniture && buildMode && <ShopInventoryButtons />}
      {isSceneReady && !roomPanel && (
        <RoomTutorial mapObjectId={mapObjectId} buildMode={buildMode}
          houseFurniture={houseFurniture} sceneRef={sceneRef} getRoomContainer={getTutorialRoomContainer}
          getViewport={getTutorialViewport} />
      )}

      {/* Preview purchase modal */}
      {previewPurchaseFurniture && (
        <PreviewPurchaseModal
          furniture={previewPurchaseFurniture}
          loading={previewPurchaseLoading}
          onBuy={handlePreviewBuy}
          onDismiss={() => { setPreviewPurchaseFurniture(null); setPreviewPurchaseInstanceId(null); }}
        />
      )}

      {/* Shared radial menu — furniture only */}
      {showRadialMenu && selectedFurniture && (
        <RadialMenuOverlay
          selectedFurniture={selectedFurniture}
          menuOptions={getRadialMenuOptions(selectedFurniture)}
          menuPosition={menuPosition}
          highlightedRadialAction={highlightedRadialAction}
          setHighlightedRadialAction={setHighlightedRadialAction}
          closeRadialMenu={closeRadialMenu}
          executeRadialAction={executeRadialAction}
        />
      )}

      {/* Just-placed Cancel/Place buttons removed — replaced by the always-available
          undo/redo controls (top-left). The green outline still clears on the next
          interaction via removeGreenOutline(). */}

      {/* Furniture shop / inventory over the room (the room stays mounted under it) */}
      <RoomPanel mapObjectId={mapObjectId} />

      {/* Onboarding modal for owner */}
      <RoomOnboardingModal isOpen={showOnboardingModal} onClose={() => setShowOnboardingModal(false)} />
    </div>
  );
};

export default RoomCanvas;
